import app from "flarum/forum/app";
import sessionLoaderSvg, { startSessionLoader, finishSessionLoader } from "./session-loader";
import { extend } from "flarum/common/extend";
import IndexPage from "flarum/forum/components/IndexPage";
import Link from "flarum/common/components/Link";
import Modal from "flarum/common/components/Modal";
import Button from "flarum/common/components/Button";
import icon from "flarum/common/helpers/icon";
import humanTime from "flarum/common/helpers/humanTime";
import LogInModal from "flarum/forum/components/LogInModal";
import SignUpModal from "flarum/forum/components/SignUpModal";
import Session from "flarum/common/Session";
import extractText from "flarum/common/utils/extractText";

/*
 * CIRKLA forum home and guest-access customizations.
 *
 * This Flarum frontend initializer owns two related features:
 * 1. The custom authenticated home dashboard.
 * 2. The guest access gate, invite-code signup flow, and post-login redirect.
 *
 * Keep behaviour-specific code close to the feature it supports. When changing
 * selectors or route rules, update the notes file shipped with this refactor.
 */

const m = window.m;

// -----------------------------------------------------------------------------
// Module state and configuration
// -----------------------------------------------------------------------------

let homeState = null;
let accessGatePrompted = false;
let lastGuestState = null;
let guestLockHeartbeat = null;
let accessGateTimer = null;
let guestRouteRedirectPending = false;
let guestStableSince = null;
let postLoginRedirectPending = false;
let guestAuthModalSwapPending = false;
let guestAuthRestoreSuppressed = false;
let protectedRouteLoginRequested = false;
const NEXT_URL_STORAGE_KEY = "cirklaNextAfterLogin";
const PROTECTED_LOGIN_INTENT_STORAGE_KEY = "cirklaProtectedLoginIntent";
const GUEST_REDIRECT_STABILITY_MS = 900;


const ANNOUNCEMENT_TAG_SLUG = "latest-news";
const EVENTS_TAG_SLUG = "events";

const LIMIT_SLIDER = 5;
const LIMIT_LIST = 10;
const LIMIT_USERS = 30;

const ACCESS_GATE_LANGS = ["en", "de", "fr"];

function safeCloseModal() {
  if (typeof app?.modal?.close !== "function") return;

  const hasModalDom =
    typeof document !== "undefined" &&
    !!document.querySelector(
      ".ModalManager .Modal, .ModalManager .modal-dialog, #modal .Modal, #modal .modal-dialog",
    );

  if (!hasModalDom) return;

  try {
    app.modal.close();
  } catch (_error) {
    // Modal transitions can leave Flarum's manager without a DOM root briefly.
  }
}

// -----------------------------------------------------------------------------
// General utilities
// -----------------------------------------------------------------------------

function isGuest() {
  return !app.session || !app.session.user;
}

function currentUserEmailConfirmed() {
  const user = app.session?.user;
  if (!user) return false;

  if (typeof user.isEmailConfirmed === "function") {
    return Boolean(user.isEmailConfirmed());
  }

  try {
    return Boolean(user.attribute?.("isEmailConfirmed"));
  } catch (_error) {
    return false;
  }
}

function currentUserEmail() {
  const user = app.session?.user;
  if (!user) return "";

  try {
    return String(user.email?.() || user.attribute?.("email") || "");
  } catch (_error) {
    return "";
  }
}

function ensureGuestBootOverlay() {
  if (typeof document === "undefined") return;

  document.body.classList.add("CirklaSessionBooting");

  if (document.querySelector(".CirklaInitialSessionLoader")) return;

  const loader = document.createElement("div");
  loader.className = "CirklaInitialSessionLoader";
  loader.setAttribute("role", "status");
  loader.setAttribute("aria-live", "polite");

  const card = document.createElement("div");
  card.className = "CirklaInitialSessionLoader-card";
  const spinner = document.createElement("div");
  spinner.className = "CirklaInitialSessionLoader-spinner";
  spinner.setAttribute("aria-hidden", "true");
  spinner.innerHTML = sessionLoaderSvg();
  const text = document.createElement("div");
  text.className = "CirklaInitialSessionLoader-text";
  text.textContent = String(trans("accessGate", "loading"));
  card.append(spinner, text);
  loader.appendChild(card);

  startSessionLoader(loader);
  document.body.appendChild(loader);
}

function clearGuestBootOverlay() {
  if (typeof document === "undefined") return;

  finishSessionLoader();
}

function guestSessionBootGraceRemaining() {
  // Initializers run before Flarum creates its forum and session models.
  // Wait only for those models, not an arbitrary post-load grace period.
  return app.forum && app.session ? 0 : 50;
}

function shouldDelayGuestAccessGatePrompt() {
  if (!isGuest()) return false;

  return guestSessionBootGraceRemaining() > 0;
}

function canShowGuestAccessGateNow() {
  return isGuest() && !shouldDelayGuestAccessGatePrompt();
}

function syncAuthBodyClasses() {
  if (typeof document === "undefined") return;

  const guest = isGuest();

  document.body.classList.toggle("CirklaGuest", guest);
  document.body.classList.toggle("CirklaSignedIn", !guest);
}

// -----------------------------------------------------------------------------
// Cross-tab session sync
// -----------------------------------------------------------------------------
//
// app.session.user is only ever read from the page's initial boot payload.
// If the user logs in or out in one tab, every other open tab keeps showing
// its old state (signed-in header, or the "sign in" gate) until something in
// that tab happens to trigger a failing request or the user manually
// refreshes. Broadcast every login/logout/expired-session event through
// localStorage's "storage" event, which every other same-origin tab
// receives automatically, and just reload those tabs - the simplest way to
// resync everything (CSRF token, cached models, forum payload) correctly.

const AUTH_BROADCAST_KEY = "cirklaAuthChangedAt";

function broadcastAuthChange() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(AUTH_BROADCAST_KEY, String(Date.now()));
  } catch (_error) {
    // Storage can be unavailable (private browsing, quota, disabled) -
    // cross-tab sync is a nice-to-have, never worth breaking the page for.
  }
}

function installCrossTabAuthSync() {
  if (typeof window === "undefined" || app.__cirklaCrossTabAuthSyncInstalled) {
    return;
  }
  app.__cirklaCrossTabAuthSyncInstalled = true;

  window.addEventListener("storage", (event) => {
    // The tab that made the change never receives its own "storage" event
    // (that's how the Web Storage API works), so this only ever fires in
    // the *other* tabs - no risk of a tab reloading itself in a loop.
    if (event.key !== AUTH_BROADCAST_KEY || !event.newValue) return;

    // Logout is a plain page navigation (window.location.href = /logout),
    // not an awaited request, so the broadcast fires the instant that
    // navigation is *started* in the other tab - well before the server has
    // actually cleared the session cookie. Reloading immediately on receipt
    // would just reload into the still-logged-in state. Give the real
    // request a moment to finish first. (Login's broadcast already waits
    // for its request to resolve, so this extra wait is harmless there -
    // just a slightly later refresh.)
    window.setTimeout(() => {
      // This tab's own guest/signed-in state (read right before reacting)
      // tells us which case we're in:
      // - Was a guest (sitting at the sign-in screen, whatever route
      //   happened to be underneath it) and someone signed in elsewhere ->
      //   land on the home page, not that incidental underlying route.
      // - Was already signed in and reading a specific page (logout
      //   happened elsewhere, or another account signed in) -> reload in
      //   place so it stays on that same page.
      if (isGuest()) {
        window.location.href = "/";
      } else {
        window.location.reload();
      }
    }, 800);
  });
}

/**
 * Broadcasts login/logout to other tabs by extending Session.prototype
 * directly, instead of monkey-patching the app.session *instance* (the
 * pattern installSessionLoginHook() below uses for its own, unrelated UI
 * purposes). The instance-patching approach only takes effect once
 * app.session actually exists at the moment it runs, which on a fresh page
 * load can be before Flarum has finished setting it up - so it silently
 * never applies until the user causes an in-app route change. Extending the
 * prototype has no such timing dependency: every Session instance, current
 * or future, picks up the wrapped behavior via the prototype chain.
 */
function installSessionBroadcastHooks() {
  if (app.__cirklaSessionBroadcastHooksInstalled) return;
  app.__cirklaSessionBroadcastHooksInstalled = true;

  extend(Session.prototype, "login", function (result) {
    if (result && typeof result.then === "function") {
      result.then(() => broadcastAuthChange());
    }
  });

  extend(Session.prototype, "logout", function () {
    broadcastAuthChange();
  });
}

function getInviteCodeFromQuery() {
  try {
    if (typeof window === "undefined") return "";

    const params = new URLSearchParams(window.location.search || "");
    return String(params.get("invite") || params.get("doorkey") || "").trim();
  } catch (error) {
    return "";
  }
}

function forumSetting(name, fallback = "") {
  const value = app.forum?.attribute?.(name);
  return value === null || value === undefined || value === "" ? fallback : value;
}

function forumBoolSetting(name, fallback = false) {
  const value = app.forum?.attribute?.(name);
  if (value === null || value === undefined || value === "") return fallback;
  return value === true || value === 1 || value === "1" || value === "true";
}

/**
 * Local copy of the cirkla.custom_pages parser also used in index.js (small
 * enough, and this file already duplicates forumSetting the same way rather
 * than importing across files). See index.js's parseCustomPageEntries for
 * the full JSON format documentation.
 */
function parseCustomPageEntriesLocal() {
  const raw = String(forumSetting("cirkla.custom_pages", "") || "");
  if (!raw.trim()) return [];

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_error) {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((group) => {
      if (!group || typeof group !== "object") return null;

      const key = String(group.key || "").trim();
      const en = group.pages?.en;
      if (!key || !en?.slug) return null;

      const enVariant = { label: en.label || key, slug: en.slug };
      const de = group.pages?.de;
      const fr = group.pages?.fr;

      return {
        key,
        isSupport: Boolean(group.isSupport) || key === "support",
        en: enVariant,
        de: de?.slug ? { label: de.label || enVariant.label, slug: de.slug } : enVariant,
        fr: fr?.slug ? { label: fr.label || enVariant.label, slug: fr.slug } : enVariant,
      };
    })
    .filter(Boolean);
}

function resolvedSupportHrefLocal() {
  const entry = parseCustomPageEntriesLocal().find((item) => item.isSupport);
  const variant = entry ? entry[currentLocale()] || entry.en : null;

  return variant?.slug ? `/p/${variant.slug}` : "/p/support";
}

function announcementTagTitle() {
  const serializedName = String(
    forumSetting("cirkla.home.announcements_tag_name", ""),
  ).trim();

  if (serializedName) return serializedName;

  const configuredTag = String(
    forumSetting("cirkla.home.announcements_tag", ANNOUNCEMENT_TAG_SLUG),
  )
    .trim()
    .toLowerCase();

  const tags = app.store?.all?.("tags") || [];
  const tag = tags.find((candidate) => {
    const slug = String(candidate?.slug?.() || "").trim().toLowerCase();
    const id = String(candidate?.id?.() || "").trim().toLowerCase();
    return slug === configuredTag || id === configuredTag;
  });

  return String(tag?.name?.() || "").trim() || t("announcementFallback");
}

function getInviteSignUpAttrs(attrs = {}) {
  const inviteCode = getInviteCodeFromQuery();
  if (!inviteCode) return attrs;

  return {
    ...attrs,
    cirklaInviteCode: inviteCode,
  };
}

function shouldAutoOpenInviteSignUp() {
  try {
    if (typeof window === "undefined") return false;

    const params = new URLSearchParams(window.location.search || "");
    return !!getInviteCodeFromQuery() && params.get("signup") !== "0";
  } catch (error) {
    return false;
  }
}

function redraw() {
  if (m && typeof m.redraw === "function") m.redraw();
}

function trans(group, key, parameters = {}) {
  const result = app.translator.trans(
    `htwsaar-htw-theme.forum.${group}.${key}`,
    parameters,
  );
  // app.translator.trans() always returns an array of vnode/text/number
  // pieces (even without params, and plural forms can yield numbers). Join
  // primitive pieces into one string so any String(trans(...)) call can't
  // insert stray commas between them; leave real vnodes (objects) alone.
  return Array.isArray(result) &&
    result.every((part) => typeof part === "string" || typeof part === "number")
    ? result.map(String).join("")
    : result;
}

function t(key, parameters = {}) {
  return trans("home", key, parameters);
}

function gateText(key, parameters = {}) {
  return trans("accessGate", key, parameters);
}

function currentLocale() {
  return String(app.data?.locale || "en").toLowerCase().split("-")[0];
}

function availableAccessGateLocales() {
  return ACCESS_GATE_LANGS.filter((locale) => app.data?.locales?.[locale]);
}

function selectLocale(locale) {
  if (!availableAccessGateLocales().includes(locale)) return;

  if (app.session?.user) {
    app.session.user
      .savePreferences({ locale })
      .then(() => window.location.reload());
    return;
  }

  const expiry = new Date();
  expiry.setFullYear(expiry.getFullYear() + 1);
  document.cookie = `locale=${locale}; path=/; expires=${expiry.toUTCString()}`;
  window.location.reload();
}

// Default per-locale slugs, used only when nobody has linked a "terms" or
// "privacy" group through the "Custom navigation pages" admin feature
// (cirkla.custom_pages - see parseCustomPageEntriesLocal below). Matches the
// same en/de/fr privacy slugs the footer already links to correctly
// (CIRKLA_FOOTER_PAGE_ROUTES in index.js) - kept here too since this file
// cannot import from index.js (see the parseCustomPageEntriesLocal comment).
const CIRKLA_LEGAL_PAGE_FALLBACK_SLUGS = {
  terms: { en: "terms" },
  privacy: {
    en: "privacy",
    de: "datenschutzerklaerung",
    fr: "politique-de-confidentialite",
  },
};

function resolvedLegalHrefLocal(key) {
  const fallbackSlugs = CIRKLA_LEGAL_PAGE_FALLBACK_SLUGS[key] || {};
  const locale = currentLocale();
  const entry = parseCustomPageEntriesLocal().find((item) => item.key === key);
  const variant = entry ? entry[locale] || entry.en : null;
  const slug = variant?.slug || fallbackSlugs[locale] || fallbackSlugs.en;

  return `/p/${slug}`;
}

function getTermsAndPrivacyLinks() {
  return {
    termsUrl: resolvedLegalHrefLocal("terms"),
    privacyUrl: resolvedLegalHrefLocal("privacy"),
  };
}

function isGuestVisibleLegalPath(path) {
  if (!path) return false;

  // Keep this list intentionally narrow: these are the only guest-readable pages.

  return (
    path === "/terms" ||
    path === "/privacy" ||
    path === "/support" ||
    path.startsWith("/terms/") ||
    path.startsWith("/privacy/") ||
    path.startsWith("/support/") ||
    path.startsWith("/p/") ||
    path.startsWith("/pages/")
  );
}

function truncateName(name, max = 10) {
  if (!name) return "";

  const value = String(name);

  return value.length > max ? `${value.slice(0, max)}...` : value;
}

function chunk(arr, size) {
  const out = [];

  for (let i = 0; i < (arr?.length || 0); i += size) {
    out.push(arr.slice(i, i + size));
  }

  return out;
}

function userRoute(user) {
  if (app.route.user) return app.route.user(user);

  return app.route("user", { username: user.username() });
}

/**
 * Non-dismissible landing modal for unauthenticated visitors.
 *
 * It intentionally does not expose site content. Users can either log in or,
 * when enabled by the forum, start the invite-code signup flow.
 */
class CirklaAccessGateModal extends Modal {
  className() {
    return "CirklaAccessGateModal Modal--small";
  }

  title() {
    return gateText("title");
  }

  content() {
    const allowSignUp = !!app.forum.attribute("allowSignUp");
    const locale = currentLocale();

    return [
      m("div", { className: "Modal-body" }, [
        m("div", { className: "CirklaAccessGate" }, [
          m("div", { className: "CirklaAccessGate-langWrap" }, [
            m("label", { className: "CirklaAccessGate-langLabel" }, [
              gateText("languageLabel"),
              m(
                "select",
                {
                  className: "CirklaAccessGate-langSelect",
                  value: locale,
                  "aria-label": gateText("languageLabel"),
                  onchange: (event) => {
                    selectLocale(String(event.target.value || "en"));
                  },
                },
                availableAccessGateLocales().map((code) =>
                  m(
                    "option",
                    { value: code },
                    app.data.locales[code],
                  ),
                ),
              ),
            ]),
            m(
              "a",
              {
                className: "CirklaAccessGate-supportLink",
                href: resolvedSupportHrefLocal(),
                onclick: (event) => {
                  event.preventDefault();
                  forceUnlockInterface();

                  safeCloseModal();

                  const href = resolvedSupportHrefLocal();

                  if (m?.route?.set) {
                    m.route.set(href);
                    return;
                  }

                  window.location.assign(`${window.location.origin}${href}`);
                },
              },
              [
                icon("fas fa-life-ring"),
                m("span", gateText("supportLink")),
              ],
            ),
          ]),
          m("div", { className: "CirklaAccessGate-badge" }, [
            icon("fas fa-lock"),
            m("span", gateText("badge")),
          ]),
          m(
            "h2",
            { className: "CirklaAccessGate-title" },
            gateText("title"),
          ),
          m("div", {
            className: "CirklaAccessGate-partners",
            role: "group",
            "aria-label": gateText("partnerGroupLabel"),
          }, [
            m("div", { className: "CirklaAccessGate-partner CirklaAccessGate-partner--project" }, [
              m("img", {
                className: "CirklaAccessGate-partnerProject",
                src: "/assets/branding/cirkla_wider_interreg_color.png",
                alt: gateText("projectPartnerAlt"),
              }),
            ]),
            m("span", {
              className: "CirklaAccessGate-partnerDivider",
              "aria-hidden": "true",
            }),
            m("div", { className: "CirklaAccessGate-partner CirklaAccessGate-partner--saarland" }, [
              m("img", {
                className: "CirklaAccessGate-partnerSaarland",
                src: "/assets/branding/saarland-ministry-mwide.jpg",
                alt: gateText("saarlandPartnerAlt"),
              }),
            ]),
            m("div", {
              className: "CirklaAccessGate-partner CirklaAccessGate-partner--wallonie",
              role: "img",
              "aria-label": gateText("walloniePartnerAlt"),
            }, [
              m("img", {
                className: "CirklaAccessGate-partnerWallonie",
                src: "/assets/branding/wallonie_h.png",
                alt: "",
                "aria-hidden": "true",
              }),
            ]),
          ]),
          m(
            "p",
            { className: "CirklaAccessGate-copy" },
            gateText("description"),
          ),
          m("div", { className: "CirklaAccessGate-points" }, [
            m("div", { className: "CirklaAccessGate-point" }, [
              icon("fas fa-user-check"),
              m("span", gateText("pointApproval")),
            ]),
            m("div", { className: "CirklaAccessGate-point" }, [
              icon("fas fa-envelope-open-text"),
              m("span", gateText("pointEmail")),
            ]),
            m("div", { className: "CirklaAccessGate-point" }, [
              icon("fas fa-shield-alt"),
              m("span", gateText("pointPrivacy")),
            ]),
          ]),
          m("div", { className: "CirklaAccessGate-actions" }, [
            m(
              Button,
              {
                className: "Button Button--primary Button--block",
                type: "button",
                onclick: () => this.openLogIn(),
              },
              gateText("login"),
            ),
            allowSignUp
              ? m(
                  Button,
                  {
                    className:
                      "Button Button--block CirklaAccessGate-secondary",
                    type: "button",
                    onclick: () => this.openSignUp(),
                  },
                  gateText("signup"),
                )
              : null,
          ]),
        ]),
      ]),
    ];
  }

  openLogIn() {
    swapGuestAuthModal(CirklaLogInModal);
  }

  openSignUp() {
    swapGuestAuthModal(CirklaSignUpModal, getInviteSignUpAttrs());
  }

  onsubmit(event) {
    event.preventDefault();
    this.openLogIn();
  }

  onready() {
    const primaryButton = this.$(".CirklaAccessGate-actions .Button--primary");
    if (primaryButton.length) primaryButton.trigger("focus");
  }
}

CirklaAccessGateModal.isDismissible = false;

// -----------------------------------------------------------------------------
// Auth modal coordination
// -----------------------------------------------------------------------------

// Swaps between the guest auth modals (access gate / log in / sign up) the
// same way Flarum core's own LogInModal.signUp()/SignUpModal.logIn() do:
// a plain app.modal.show(component, attrs) with stackModal left false, so
// ModalManagerState.show() replaces modalList itself and Mithril unmounts
// the old modal and mounts the new one through its own diffing. An earlier
// version of this function passed stackModal=true (which pushes onto
// modalList instead of replacing it) and then manually removeChild'd the
// previous modal's DOM to compensate - that raw removal raced with
// Mithril's own reconciliation of the same nodes and threw
// `NotFoundError: Failed to execute 'insertBefore'/'removeChild'`,
// crashing the in-flight redraw and leaving the modal stuck (the same
// failure mode fixed elsewhere in this file for the invite-member/calendar
// modals).
function swapGuestAuthModal(component, attrs = {}) {
  if (!app.modal || typeof app.modal.show !== "function") return;

  guestAuthModalSwapPending = true;
  app.modal.show(component, attrs);
  setTimeout(() => {
    guestAuthModalSwapPending = false;
  }, 0);
}

function removeDuplicateAccessGateModals() {
  if (typeof document === "undefined") return;

  const gates = Array.from(
    document.querySelectorAll(".ModalManager .CirklaAccessGateModal"),
  );

  gates.slice(0, -1).forEach((node) => {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  });
}

function showAccessGateModal() {
  if (!canShowGuestAccessGateNow()) return;
  if (!app.modal || typeof app.modal.show !== "function") return;

  clearGuestBootOverlay();

  if (hasGuestAuthModalOpen()) {
    removeDuplicateAccessGateModals();
    return;
  }

  removeDuplicateAccessGateModals();
  app.modal.show(CirklaAccessGateModal, {}, true);
  setTimeout(removeDuplicateAccessGateModals, 0);
}

/**
 * Renders a password <input> with a show/hide eye-icon toggle button, used
 * in place of core's plain password field in both the log-in and sign-up
 * modals below. Toggling only ever flips the input's `type` between
 * "password" and "text" client-side - the value itself is untouched.
 */
function renderPasswordField({
  name,
  autocomplete,
  placeholder,
  bidi,
  disabled,
  showPassword,
  onToggle,
}) {
  return m("div", { className: "Form-group CirklaPasswordField" }, [
    m("input", {
      className: "FormControl",
      name,
      type: showPassword ? "text" : "password",
      autocomplete,
      placeholder,
      "aria-label": placeholder,
      bidi,
      disabled,
    }),
    m(
      "button",
      {
        type: "button",
        className: "CirklaPasswordField-toggle",
        "aria-label": trans("password", showPassword ? "hide" : "show"),
        tabindex: -1,
        disabled,
        onclick: (event) => {
          event.preventDefault();
          onToggle();
        },
      },
      icon(showPassword ? "fas fa-eye-slash" : "fas fa-eye"),
    ),
  ]);
}

class CirklaLogInModal extends LogInModal {
  oninit(vnode) {
    super.oninit(vnode);
    this.cirklaShowPassword = false;
  }

  fields() {
    const items = super.fields();

    if (typeof items.remove === "function") {
      const passwordLabel = extractText(
        app.translator.trans("core.forum.log_in.password_placeholder"),
      );

      items.remove("password");
      items.add(
        "password",
        renderPasswordField({
          name: "password",
          autocomplete: "current-password",
          placeholder: passwordLabel,
          bidi: this.password,
          disabled: this.loading,
          showPassword: !!this.cirklaShowPassword,
          onToggle: () => {
            this.cirklaShowPassword = !this.cirklaShowPassword;
          },
        }),
        20,
      );
    }

    return items;
  }

  signUp() {
    const identification = this.identification();
    const attrs = {
      [identification.includes("@") ? "email" : "username"]: identification,
    };

    swapGuestAuthModal(CirklaSignUpModal, getInviteSignUpAttrs(attrs));
  }

  hide() {
    super.hide();
    scheduleGuestAccessGateRestore();
  }
}

CirklaLogInModal.isDismissibleViaCloseButton = true;
CirklaLogInModal.isDismissibleViaBackdropClick = false;
CirklaLogInModal.isDismissibleViaEscKey = false;

let expiredSessionRedirectHandled = false;

/**
 * The server-side session can die (cookie expiry, restart, etc.) while a
 * page is still open. app.session.user stays populated from the initial
 * page load in that case, so the normal guest checks never trip - the
 * private-messages chat just keeps retrying its API calls and failing with
 * 403/404 forever. Call this once that failure pattern is detected.
 *
 * Note: this deliberately does NOT null out app.session.user. Several
 * mounted widgets (e.g. the private-messages unread-count badge in the
 * header) read app.session.user directly without a null guard, assuming a
 * logged-in session always has one; nulling it out from under them while
 * they're still mounted throws on every redraw and blanks the whole page,
 * which is worse than the original bug. Just navigate away (to unmount the
 * broken chat and stop its retries) and show the login form on top - a
 * successful login reloads the page anyway, which is what actually clears
 * the stale session state.
 */
export function handleExpiredSessionDetected() {
  if (expiredSessionRedirectHandled) return;
  expiredSessionRedirectHandled = true;

  // Same browser, same cookie jar - if this tab's session died, any other
  // open tab is almost certainly in the same state. Let them know instead
  // of leaving each one to discover it independently on its own next
  // failed request.
  broadcastAuthChange();

  if (m?.route && typeof m.route.set === "function") {
    m.route.set("/");
  }

  swapGuestAuthModal(CirklaLogInModal);
}

/**
 * Signup modal wrapper that adds CIRKLA-specific requirements:
 * invite code, Terms acceptance, Privacy acceptance, and translated consent UI.
 */
class CirklaSignUpModal extends SignUpModal {
  oninit(vnode) {
    super.oninit(vnode);
    this.cirklaConsentAccepted = false;
    this.cirklaConsentError = null;
    this.cirklaInviteError = null;
    this.cirklaShowPassword = false;

    const inviteCode = String(
      this.attrs.cirklaInviteCode || getInviteCodeFromQuery() || "",
    ).trim();
    if (inviteCode && typeof this.doorkey === "function") {
      this.doorkey(inviteCode);
    }
  }

  canSubmitWithConsent() {
    return !!this.cirklaConsentAccepted;
  }

  fields() {
    const items = super.fields();

    if (typeof items.remove === "function") {
      items.remove("fof-terms-legal-text");
      (app.store?.all?.("fof-terms-policies") || []).forEach((policy) => {
        if (policy?.id?.()) {
          items.remove(`fof-terms-policy-${policy.id()}`);
        }
      });
      items.remove("doorkey");
      items.remove("submit");

      if (items.has("password") && !this.attrs.token) {
        const passwordLabel = extractText(
          app.translator.trans("core.forum.sign_up.password_placeholder"),
        );

        items.remove("password");
        items.add(
          "password",
          renderPasswordField({
            name: "password",
            autocomplete: "new-password",
            placeholder: passwordLabel,
            bidi: this.password,
            disabled: this.loading,
            showPassword: !!this.cirklaShowPassword,
            onToggle: () => {
              this.cirklaShowPassword = !this.cirklaShowPassword;
            },
          }),
          10,
        );
      }
    }

    items.add(
      "doorkey",
      m("div", { className: "Form-group CirklaInviteCodeField" }, [
        m("input", {
          className: "FormControl",
          name: "fof-doorkey",
          type: "text",
          placeholder: gateText("inviteCode"),
          autocomplete: "one-time-code",
          required: true,
          bidi: this.doorkey,
          disabled: this.loading,
          oninput: () => {
            this.cirklaInviteError = null;
          },
        }),
        this.cirklaInviteError
          ? m(
              "p",
              { className: "helpText CirklaConsentError" },
              this.cirklaInviteError,
            )
          : null,
      ]),
      20,
    );

    const { privacyUrl } = getTermsAndPrivacyLinks();

    items.add(
      "cirkla-consent",
      m("div", { className: "Form-group CirklaConsentBlock" }, [
        m(
          "div",
          { className: "CirklaConsentBlock-title" },
          gateText("consentTitle"),
        ),
        m(
          "div",
          { className: "CirklaConsentBlock-text" },
          gateText("consentText"),
        ),
        m("label", { className: "checkbox CirklaConsentCheck" }, [
          m("input", {
            type: "checkbox",
            checked: !!this.cirklaConsentAccepted,
            disabled: this.loading,
            onchange: () => {
              this.cirklaConsentAccepted = !this.cirklaConsentAccepted;
              this.cirklaConsentError = null;
            },
          }),
          m("span", [
            gateText("consentCheckboxPrefix"),
            m(
              "a",
              {
                href: privacyUrl,
                target: "_blank",
                rel: "noopener noreferrer",
                onclick: (event) => event.stopPropagation(),
              },
              gateText("consentCheckboxLinkText"),
            ),
            gateText("consentCheckboxSuffix"),
          ]),
        ]),
        this.cirklaConsentError
          ? m(
              "p",
              { className: "helpText CirklaConsentError" },
              this.cirklaConsentError,
            )
          : null,
      ]),
      0,
    );

    items.add(
      "submit",
      m("div", { className: "Form-group" }, [
        m(
          Button,
          {
            className: "Button Button--primary Button--block",
            type: "submit",
            loading: this.loading,
            disabled: this.loading || !this.canSubmitWithConsent(),
          },
          app.translator.trans("core.forum.sign_up.submit_button"),
        ),
      ]),
      -10,
    );

    return items;
  }

  logIn() {
    const attrs = {
      identification: this.email() || this.username(),
    };

    swapGuestAuthModal(CirklaLogInModal, attrs);
  }

  hide() {
    super.hide();
    scheduleGuestAccessGateRestore();
  }

  submitData() {
    const data = super.submitData();
    const { termsUrl, privacyUrl } = getTermsAndPrivacyLinks();
    const policies = app.store?.all?.("fof-terms-policies") || [];

    data.cirklaAcceptTerms = !!this.cirklaConsentAccepted;
    data.cirklaAcceptPrivacy = !!this.cirklaConsentAccepted;
    data.cirklaTermsUrl = termsUrl || "/p/terms";
    data.cirklaPrivacyUrl = privacyUrl || "/p/privacy";
    data.cirklaConsentLanguage = currentLocale();
    data["fof-doorkey"] =
      typeof this.doorkey === "function"
        ? String(this.doorkey() || "").trim()
        : "";

    policies.forEach((policy) => {
      const policyId = policy?.id?.();
      const name = String(policy?.name?.() || "").toLowerCase();
      const url = String(policy?.url?.() || "").toLowerCase();

      if (!policyId) return;

      if (
        /(term|condition|bedingungen|agb|conditions)/i.test(name) ||
        /(term|condition)/i.test(url)
      ) {
        data[`fof_terms_policy_${policyId}`] = !!this.cirklaConsentAccepted;
      }

      if (
        /(privacy|datenschutz|confidentialit|donnees personnelles)/i.test(
          name,
        ) ||
        /privacy/i.test(url)
      ) {
        data[`fof_terms_policy_${policyId}`] = !!this.cirklaConsentAccepted;
      }
    });

    return data;
  }

  onsubmit(event) {
    const inviteCode =
      typeof this.doorkey === "function"
        ? String(this.doorkey() || "").trim()
        : "";

    if (!inviteCode) {
      event.preventDefault();
      this.cirklaInviteError = gateText("inviteCodeError");
      redraw();
      return;
    }

    if (!this.canSubmitWithConsent()) {
      event.preventDefault();
      this.cirklaConsentError = gateText("consentError");
      redraw();
      return;
    }

    this.cirklaConsentError = null;
    super.onsubmit(event);
  }
}

CirklaSignUpModal.isDismissibleViaBackdropClick = false;
CirklaSignUpModal.isDismissibleViaEscKey = false;

// -----------------------------------------------------------------------------
// Guest lock and redirect handling
// -----------------------------------------------------------------------------

function promptAccessGateOnce() {
  if (accessGatePrompted) return;

  accessGatePrompted = true;

  if (accessGateTimer) {
    clearTimeout(accessGateTimer);
  }

  const delay = guestSessionBootGraceRemaining();

  accessGateTimer = setTimeout(() => {
    accessGateTimer = null;

    if (!canShowGuestAccessGateNow()) {
      accessGatePrompted = false;
      return;
    }

    if (!app.modal || typeof app.modal.show !== "function") {
      accessGatePrompted = false;
      return;
    }

    clearGuestBootOverlay();

    const shouldOpenLogin = consumeProtectedRouteLoginIntent();

    if (hasGuestAuthModalOpen()) {
      removeDuplicateAccessGateModals();
    } else if (
      app.forum.attribute("allowSignUp") &&
      shouldAutoOpenInviteSignUp()
    ) {
      app.modal.show(CirklaSignUpModal, getInviteSignUpAttrs(), true);
    } else if (shouldOpenLogin) {
      app.modal.show(CirklaLogInModal, {}, true);
    } else {
      showAccessGateModal();
    }
  }, delay);
}

function restoreAccessGateForGuest(options = {}) {
  if (typeof document === "undefined") return;
  if (guestAuthRestoreSuppressed || guestAuthModalSwapPending) return;
  if (!isGuest()) return;

  const { immediate = false } = options;
  const relativePath = getCurrentRelativePath();
  if (shouldDelayGuestAccessGatePrompt()) {
    ensureGuestBootOverlay();
    setTimeout(() => restoreAccessGateForGuest(options), 250);
    return;
  }

  if (isGuestVisibleLegalPath(relativePath)) {
    document.body.classList.remove("CirklaGuestLocked");
    forceUnlockInterface();
    return;
  }

  document.body.classList.add("CirklaGuestLocked");

  const reopen = () => {
    if (
      guestAuthRestoreSuppressed ||
      guestAuthModalSwapPending ||
      !isGuest() ||
      isGuestVisibleLegalPath(getCurrentRelativePath()) ||
      !app.modal ||
      typeof app.modal.show !== "function"
    ) {
      return;
    }

    if (hasGuestAuthModalOpen()) {
      removeDuplicateAccessGateModals();
      return;
    }

    accessGatePrompted = true;
    showAccessGateModal();
  };

  if (immediate) {
    reopen();
    return;
  }

  setTimeout(reopen, 0);
  setTimeout(reopen, 90);
}

function scheduleGuestAccessGateRestore() {
  if (guestAuthRestoreSuppressed || guestAuthModalSwapPending || !isGuest())
    return;

  setTimeout(() => {
    if (guestAuthRestoreSuppressed || guestAuthModalSwapPending || !isGuest())
      return;
    restoreAccessGateForGuest({ immediate: true });
  }, 340);
}

function hasGuestAuthModalOpen() {
  if (typeof document === "undefined") return false;

  const authModals = document.querySelectorAll(
    ".ModalManager .CirklaAccessGateModal, .ModalManager .LogInModal, .ModalManager .SignUpModal",
  );

  return Array.from(authModals).some((el) => {
    const style = window.getComputedStyle(el);
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0"
    );
  });
}

function forceUnlockInterface() {
  if (typeof document === "undefined") return;

  document.body.classList.remove("CirklaGuestLocked");

  document
    .querySelectorAll(
      ".ModalManager .CirklaAccessGateModal, .ModalManager .LogInModal, .ModalManager .SignUpModal",
    )
    .forEach((node) => {
      if (node && node.parentNode) node.parentNode.removeChild(node);
    });

  const hasAnyVisibleModal = Array.from(
    document.querySelectorAll(".ModalManager .Modal"),
  ).some((el) => {
    const style = window.getComputedStyle(el);
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0"
    );
  });

  if (!hasAnyVisibleModal) {
    document
      .querySelectorAll(".Modal-backdrop, .ModalManager-invisibleBackdrop")
      .forEach((node) => {
        if (node && node.parentNode) node.parentNode.removeChild(node);
      });
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");

    // The nodes above were just removed directly instead of through
    // Mithril/Flarum's own modal lifecycle, so app.modal (Flarum's
    // ModalManagerState) never finds out. Its `backdropShown` flag in
    // particular is normally only cleared by a CSS transitionend event on
    // the backdrop element - which never fires for a node that's already
    // gone. Left stale, the next time a modal needs to open, Mithril's
    // redraw diffs against vnodes it still thinks are mounted and throws a
    // console-only "insertBefore" error that aborts the patch, leaving the
    // page blank (App-content is hidden while guest-locked, and with no
    // modal on top there's nothing to see) until a hard reload resets both
    // Mithril's and Flarum's state. Reset it here so redraws stay in sync.
    if (app?.modal) {
      app.modal.modal = null;
      app.modal.modalList = [];
      app.modal.backdropShown = false;

      if (typeof m?.redraw === "function") m.redraw();
    }
  }

  document
    .querySelectorAll(".App-header, .App-content, .App-footer, .CirklaFooter")
    .forEach((el) => {
      el.style.visibility = "visible";
      el.style.pointerEvents = "auto";
    });
}

function getBasePathname() {
  const baseUrl = app.forum?.attribute?.("baseUrl");
  if (!baseUrl || typeof window === "undefined") return "";

  try {
    return new URL(baseUrl, window.location.origin).pathname.replace(
      /\/+$/,
      "",
    );
  } catch (error) {
    return "";
  }
}

function getCurrentRelativePath() {
  if (typeof window === "undefined") return "/";

  const basePath = getBasePathname();
  const pathname = window.location.pathname || "/";
  const normalizedPath = pathname.startsWith(basePath)
    ? pathname.slice(basePath.length) || "/"
    : pathname;

  return normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
}

function sanitizeNextUrl(candidate) {
  if (!candidate) return null;

  // Only allow same-origin relative redirects. This prevents open redirects.
  const value = String(candidate).trim();
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

function readNextUrlFromQuery() {
  if (
    typeof window === "undefined" ||
    !window.location ||
    typeof window.URLSearchParams === "undefined"
  ) {
    return null;
  }

  const params = new URLSearchParams(window.location.search || "");
  return sanitizeNextUrl(params.get("next"));
}

function clearNextUrlQueryParam() {
  if (
    typeof window === "undefined" ||
    !window.location ||
    typeof window.URLSearchParams === "undefined"
  ) {
    return;
  }

  const params = new URLSearchParams(window.location.search || "");
  const hadAuthParams =
    params.has("next") || params.has("login") || params.has("auth");

  if (!hadAuthParams) return;

  params.delete("next");
  params.delete("login");
  params.delete("auth");

  if (window.history && typeof window.history.replaceState === "function") {
    const search = params.toString();
    const cleanUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }
}

function peekStoredNextUrl() {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  return sanitizeNextUrl(window.sessionStorage.getItem(NEXT_URL_STORAGE_KEY));
}

function clearStoredNextUrl() {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  window.sessionStorage.removeItem(NEXT_URL_STORAGE_KEY);
}

function readProtectedLoginIntentFromQuery() {
  if (
    typeof window === "undefined" ||
    !window.location ||
    typeof window.URLSearchParams === "undefined"
  ) {
    return false;
  }

  const params = new URLSearchParams(window.location.search || "");

  return params.get("login") === "1" || params.get("auth") === "login";
}

function markProtectedRouteLoginIntent() {
  protectedRouteLoginRequested = true;

  if (typeof window === "undefined" || !window.sessionStorage) return;

  window.sessionStorage.setItem(PROTECTED_LOGIN_INTENT_STORAGE_KEY, "1");
}

function consumeProtectedRouteLoginIntent() {
  let stored = false;

  if (typeof window !== "undefined" && window.sessionStorage) {
    stored =
      window.sessionStorage.getItem(PROTECTED_LOGIN_INTENT_STORAGE_KEY) === "1";

    window.sessionStorage.removeItem(PROTECTED_LOGIN_INTENT_STORAGE_KEY);
  }

  const requested = protectedRouteLoginRequested || stored;
  protectedRouteLoginRequested = false;

  return requested;
}

function saveNextUrlForLogin() {
  if (typeof window === "undefined" || !window.sessionStorage) return;

  const next = sanitizeNextUrl(
    `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`,
  );
  if (next && next !== "/") {
    window.sessionStorage.setItem(NEXT_URL_STORAGE_KEY, next);
  }
}

function consumeNextUrlAfterLogin() {
  const next = peekStoredNextUrl();
  if (next) clearStoredNextUrl();
  return next;
}

function captureNextUrlFromQuery() {
  const next = readNextUrlFromQuery();
  const loginIntent = readProtectedLoginIntentFromQuery();

  if (next && window.sessionStorage && next !== "/") {
    window.sessionStorage.setItem(NEXT_URL_STORAGE_KEY, next);
  }

  if ((next && next !== "/") || loginIntent) {
    markProtectedRouteLoginIntent();
  }

  if (next || loginIntent) {
    clearNextUrlQueryParam();
  }
}

function redirectToNextAfterLogin(options = {}) {
  if (typeof window === "undefined" || !window.location) return false;
  if (postLoginRedirectPending) return true;

  const { allowStored = false } = options;
  const queryNext = readNextUrlFromQuery();
  const storedNext = allowStored ? consumeNextUrlAfterLogin() : null;
  const nextUrl = queryNext || storedNext;

  if (!nextUrl) return false;

  const currentPath = `${window.location.pathname || "/"}${window.location.hash || ""}`;
  if (
    nextUrl === currentPath ||
    nextUrl === (window.location.pathname || "/")
  ) {
    clearNextUrlQueryParam();
    if (allowStored) clearStoredNextUrl();
    return false;
  }

  clearNextUrlQueryParam();
  if (allowStored) clearStoredNextUrl();

  postLoginRedirectPending = true;
  setTimeout(() => window.location.assign(nextUrl), 40);
  setTimeout(() => {
    postLoginRedirectPending = false;
  }, 1800);

  return true;
}

function getForumHomeUrl() {
  if (typeof window === "undefined") return "/";
  return app.forum?.attribute?.("baseUrl") || window.location.origin;
}

function redirectGuestToIndexIfNeeded() {
  if (typeof window === "undefined") return false;

  const relativePath = getCurrentRelativePath();
  if (!relativePath || relativePath === "/") return false;
  if (isGuestVisibleLegalPath(relativePath)) return false;
  if (guestRouteRedirectPending) return true;

  guestRouteRedirectPending = true;
  saveNextUrlForLogin();
  markProtectedRouteLoginIntent();

  window.location.replace(getForumHomeUrl());

  setTimeout(() => {
    guestRouteRedirectPending = false;
  }, 320);

  return true;
}

/**
 * Central guest access policy.
 *
 * Public pages are limited to legal/support content. All other routes are locked
 * behind the access gate and, after a short stability window, guests are routed
 * back to the forum home while their intended destination is stored for login.
 */
function enforceGlobalGuestLock() {
  if (typeof document === "undefined") return;
  syncAuthBodyClasses();

  const guest = isGuest();
  const wasGuest = lastGuestState;
  const transitionedToAuthenticated = wasGuest === true && guest === false;
  const currentRelativePath = getCurrentRelativePath();
  const guestCanViewCurrentPath = isGuestVisibleLegalPath(currentRelativePath);
  lastGuestState = guest;

  if (!guest) {
    clearGuestBootOverlay();
    guestStableSince = null;
    if (accessGateTimer) {
      clearTimeout(accessGateTimer);
      accessGateTimer = null;
    }

    // Deliberately narrow: only treat this as guest-lock residue that needs
    // forced cleanup, never "some modal happens to be open right now" (that
    // used to also match `.Modal-backdrop`/`.modal-open`, which are just as
    // true for a signed-in user's own modal - e.g. Invite Member - as for a
    // leftover guest gate. Since this heartbeat runs continuously for every
    // signed-in user, that false match called forceUnlockInterface() on any
    // legitimate modal moments after it opened, ripping out its backdrop and
    // resetting app.modal's state out from under it before the user could
    // even see it.
    const needsCleanup =
      transitionedToAuthenticated ||
      document.body.classList.contains("CirklaGuestLocked") ||
      hasGuestAuthModalOpen();

    if (needsCleanup) safeCloseModal();
    if (needsCleanup) {
      forceUnlockInterface();
      setTimeout(forceUnlockInterface, 0);
      setTimeout(forceUnlockInterface, 120);
    }

    accessGatePrompted = false;
    if (
      redirectToNextAfterLogin({ allowStored: transitionedToAuthenticated })
    ) {
      return;
    }
    return;
  }

  if (!guestStableSince) guestStableSince = Date.now();

  if (guestCanViewCurrentPath) {
    clearGuestBootOverlay();
    if (accessGateTimer) {
      clearTimeout(accessGateTimer);
      accessGateTimer = null;
    }

    accessGatePrompted = false;
    document.body.classList.remove("CirklaGuestLocked");
    forceUnlockInterface();
    return;
  }

  // Only legal/support policy pages are public. Every other route, including
  // the index page, stays behind the access gate until Flarum has a user in
  // the session. This prevents a slow session bootstrap from flashing content.
  document.body.classList.add("CirklaGuestLocked");

  const guestIsStable =
    Date.now() - guestStableSince >= GUEST_REDIRECT_STABILITY_MS;

  if (guestIsStable && redirectGuestToIndexIfNeeded()) {
    if (!accessGatePrompted) promptAccessGateOnce();
    return;
  }

  /*
   * Do not show the login/access modal during initial session boot.
   * This prevents a logged-in user from briefly seeing the guest login popup.
   */
  if (shouldDelayGuestAccessGatePrompt()) {
    ensureGuestBootOverlay();

    if (!accessGatePrompted) {
      promptAccessGateOnce();
    }

    return;
  }

  if (!hasGuestAuthModalOpen() && !accessGatePrompted) {
    promptAccessGateOnce();
  }
}

/**
 * Patches Flarum session login once so the UI is unlocked and the stored
 * destination is restored immediately after successful authentication.
 */
function installSessionLoginHook() {
  if (!app.session || app.session.__cirklaGuestLockPatched) return;

  if (typeof app.session.login === "function") {
    const originalLogin = app.session.login.bind(app.session);
    app.session.login = function () {
      const result = originalLogin.apply(this, arguments);

      if (result && typeof result.then === "function") {
        return result.then((value) => {
          guestAuthRestoreSuppressed = true;
          accessGatePrompted = false;
          lastGuestState = false;
          safeCloseModal();
          forceUnlockInterface();
          setTimeout(enforceGlobalGuestLock, 0);
          setTimeout(enforceGlobalGuestLock, 120);
          setTimeout(() => {
            guestAuthRestoreSuppressed = false;
          }, 300);
          if (typeof window !== "undefined" && window.location) {
            if (!redirectToNextAfterLogin({ allowStored: true })) {
              setTimeout(() => window.location.reload(), 60);
            }
          }
          return value;
        });
      }

      setTimeout(enforceGlobalGuestLock, 0);
      return result;
    };
  }

  app.session.__cirklaGuestLockPatched = true;
}

// -----------------------------------------------------------------------------
// Guest placeholder content
// -----------------------------------------------------------------------------

function guestShell() {
  return m(
    "section",
    { className: "CirklaGuestShell CirklaCard", key: "cirkla-guest-shell" },
    [
      m("div", { className: "CirklaGuestShell-badge" }, [
        icon("fas fa-lock"),
        m("span", gateText("badge")),
      ]),
      m(
        "h2",
        { className: "CirklaGuestShell-title" },
        gateText("title"),
      ),
      m(
        "p",
        { className: "CirklaGuestShell-copy" },
        gateText("description"),
      ),
    ],
  );
}

function emailConfirmationShell() {
  const email = currentUserEmail();

  return m(
    "section",
    {
      className: "CirklaGuestShell CirklaEmailConfirmationShell",
      key: "cirkla-email-confirmation",
    },
    [
      m(".CirklaGuestShell-copy", [
        m(".CirklaAccessGate-badge", [
          icon("fas fa-envelope-open-text"),
          m("span", trans("verification", "badge")),
        ]),
        m("h1", trans("verification", "title")),
        m(
          "p",
          email
            ? trans("verification", "sentTo", { email })
            : trans("verification", "description"),
        ),
        m("p", trans("verification", "nextStep")),
      ]),
    ],
  );
}

// -----------------------------------------------------------------------------
// Home dashboard components
// -----------------------------------------------------------------------------

class CirklaCarousel {
  oninit(vnode) {
    this.items = vnode.attrs.items || [];
    this.idx = 0;
    this.timer = null;
  }

  oncreate() {
    this.timer = setInterval(() => {
      const total = Math.min(this.items?.length || 0, LIMIT_SLIDER);
      if (total <= 1) return;
      this.idx = (this.idx + 1) % total;
      redraw();
    }, 6500);
  }

  onremove() {
    if (this.timer) clearInterval(this.timer);
  }

  view(vnode) {
    const items = vnode.attrs.items || [];
    const loading = !!vnode.attrs.loading;
    this.items = items;

    const total = Math.min(items.length, LIMIT_SLIDER);
    if (total > 0 && this.idx >= total) this.idx = 0;

    const head = m("div", { className: "CirklaCarousel-head" }, [
      m(
        "div",
        { className: "CirklaCard-title" },
        announcementTagTitle(),
      ),
      total > 1
        ? m("div", { className: "CirklaCarousel-controls" }, [
            m(
              "button",
              {
                className: "Button Button--icon CirklaIconBtn",
                type: "button",
                "aria-label": t("previousAnnouncement"),
                title: t("previousAnnouncement"),
                onclick: () => {
                  this.idx = (this.idx - 1 + total) % total;
                  redraw();
                },
              },
              icon("fas fa-chevron-left"),
            ),
            m(
              "button",
              {
                className: "Button Button--icon CirklaIconBtn",
                type: "button",
                "aria-label": t("nextAnnouncement"),
                title: t("nextAnnouncement"),
                onclick: () => {
                  this.idx = (this.idx + 1) % total;
                  redraw();
                },
              },
              icon("fas fa-chevron-right"),
            ),
          ])
        : null,
    ]);

    if (!total) {
      return m(
        "div",
        {
          className: "CirklaCard CirklaCarousel",
          style: { width: "100%", maxWidth: "100%", minWidth: "0" },
        },
        [
          head,
          m("div", { className: "CirklaCarousel-body is-empty" }, [
            m(
              "div",
              { className: "CirklaMuted" },
              loading
                ? t("loading")
                : t("emptyAnnouncements"),
            ),
          ]),
        ],
      );
    }

    const cur = items[this.idx % total];
    const user = cur.user?.();
    const created = cur.createdAt?.() || cur.lastPostedAt?.();
    const title = cur.title?.() || "";

    return m(
      "div",
      {
        className: "CirklaCard CirklaCarousel",
        style: { width: "100%", maxWidth: "100%", minWidth: "0" },
      },
      [
        head,
        m(
          Link,
          { href: app.route.discussion(cur), className: "CirklaCarousel-body" },
          [
            m("div", { className: "CirklaCarousel-kicker" }, [
              m("span", { className: "CirklaPill" }, t("featured")),
              user
                ? m(
                    "span",
                    { className: "CirklaMuted" },
                    user.displayName?.() || user.username?.(),
                  )
                : null,
              created
                ? m("span", { className: "CirklaMuted" }, humanTime(created))
                : null,
            ]),
            m("div", { className: "CirklaCarousel-title", title }, title),
            m(
              "div",
              { className: "CirklaCarousel-sub" },
              t("clickToOpen"),
            ),
          ],
        ),
        m("div", { className: "CirklaCarousel-footer" }, [
          m(
            "div",
            { className: "CirklaMuted" },
            t("pagination", { current: this.idx + 1, total }),
          ),
        ]),
      ],
    );
  }
}

class CirklaSmallDiscussionCarousel {
  oninit(vnode) {
    this.items = vnode.attrs.items || [];
    this.idx = 0;
    this.expanded = false;
  }

  view(vnode) {
    const cardTitle = vnode.attrs.title || "";
    const items = vnode.attrs.items || [];
    const loading = !!vnode.attrs.loading;

    this.items = items;

    const titleLines = vnode.attrs.titleLines || 3;
    const total = Math.min(items.length, LIMIT_LIST);
    if (total > 0 && this.idx >= total) this.idx = 0;

    const head = m("div", { className: "CirklaSmallHead" }, [
      m("div", { className: "CirklaCard-title" }, cardTitle),
      m("div", { className: "CirklaSmallCtrls" }, [
        m(
          "button",
          {
            className: "Button Button--icon CirklaIconBtn",
            type: "button",
            "aria-label": t("previousDiscussion"),
            title: t("previousDiscussion"),
            disabled: total <= 1,
            onclick: () => {
              if (total <= 1) return;
              this.idx = (this.idx - 1 + total) % total;
              redraw();
            },
          },
          icon("fas fa-chevron-left"),
        ),
        m(
          "button",
          {
            className: "Button Button--icon CirklaIconBtn",
            type: "button",
            "aria-label": t("nextDiscussion"),
            title: t("nextDiscussion"),
            disabled: total <= 1,
            onclick: () => {
              if (total <= 1) return;
              this.idx = (this.idx + 1) % total;
              redraw();
            },
          },
          icon("fas fa-chevron-right"),
        ),
      ]),
    ]);

    const cur = total ? items[this.idx % total] : null;
    const user = cur?.user?.();
    const created = cur?.createdAt?.() || cur?.lastPostedAt?.();
    const fullTitle = cur?.title?.() || "";

    return m(
      "div",
      {
        className: "CirklaCard CirklaSmallCarousel",
        style: { width: "100%", maxWidth: "100%", minWidth: "0" },
      },
      [
        head,
        m("div", { className: "CirklaSmallContent" }, [
          total
            ? m(
                Link,
                {
                  href: app.route.discussion(cur),
                  className: "CirklaSmallBody",
                },
                [
                  m("div", { className: "CirklaSmallMain" }, [
                    m(
                      "div",
                      {
                        className: `CirklaSmallTopicTitle${this.expanded ? " is-expanded" : ""}`,
                        title: fullTitle,
                        style: this.expanded
                          ? ""
                          : `--cirkla-title-lines:${titleLines}`,
                        onclick: (event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          this.expanded = !this.expanded;
                          redraw();
                        },
                      },
                      fullTitle,
                    ),
                    m("div", { className: "CirklaSmallMeta" }, [
                      m(
                        "span",
                        user
                          ? user.displayName?.() || user.username?.()
                          : "-",
                      ),
                      created
                        ? m("span", { className: "CirklaSmallDot" }, "-")
                        : null,
                      created ? humanTime(created) : null,
                    ]),
                  ]),
                  m(
                    "div",
                    { className: "CirklaSmallMore" },
                    t("clickToOpen"),
                  ),
                ],
              )
            : m(
                "div",
                { className: "CirklaMuted CirklaSmallStatus" },
                loading ? t("loading") : t("emptyDiscussions"),
              ),
        ]),
        m("div", { className: "CirklaSmallFooter" }, [
          m(
            "div",
            { className: "CirklaMuted" },
            t("pagination", {
              current: total ? this.idx + 1 : 0,
              total,
            }),
          ),
        ]),
      ],
    );
  }
}

class CirklaUsersCarousel {
  oninit(vnode) {
    this.users = vnode.attrs.users || [];
    this.idx = 0;
  }

  view(vnode) {
    const title = vnode.attrs.title || t("topContributors");
    const users = vnode.attrs.users || [];
    const loading = !!vnode.attrs.loading;
    this.users = users;

    const top = users.slice(0, LIMIT_USERS);
    const slides = chunk(top, 3);
    if (slides.length > 0 && this.idx >= slides.length) this.idx = 0;

    const page = slides[this.idx] || [];

    return m(
      "div",
      {
        className: "CirklaCard CirklaSmallCarousel",
        style: { width: "100%", maxWidth: "100%", minWidth: "0" },
      },
      [
        m("div", { className: "CirklaSmallHead" }, [
          m("div", { className: "CirklaCard-title" }, title),
          m("div", { className: "CirklaSmallCtrls" }, [
            m(
              "button",
              {
                className: "Button Button--icon CirklaIconBtn",
                type: "button",
                "aria-label": t("previousContributor"),
                title: t("previousContributor"),
                disabled: slides.length <= 1,
                onclick: () => {
                  if (slides.length <= 1) return;
                  this.idx = (this.idx - 1 + slides.length) % slides.length;
                  redraw();
                },
              },
              icon("fas fa-chevron-left"),
            ),
            m(
              "button",
              {
                className: "Button Button--icon CirklaIconBtn",
                type: "button",
                "aria-label": t("nextContributor"),
                title: t("nextContributor"),
                disabled: slides.length <= 1,
                onclick: () => {
                  if (slides.length <= 1) return;
                  this.idx = (this.idx + 1) % slides.length;
                  redraw();
                },
              },
              icon("fas fa-chevron-right"),
            ),
          ]),
        ]),
        m("div", { className: "CirklaSmallContent" }, [
          !top.length
            ? m(
                "div",
                { className: "CirklaMuted CirklaSmallStatus" },
                loading ? t("loading") : t("emptyContributors"),
              )
            : m("div", { className: "CirklaUsersSlide" }, [
                page.map((user, i) =>
                  m(
                    Link,
                    { href: userRoute(user), className: "CirklaUserRow" },
                    [
                      m(
                        "div",
                        { className: "CirklaRank" },
                        String(this.idx * 3 + i + 1).padStart(2, "0"),
                      ),
                      m(
                        "div",
                        { className: "CirklaUser-name" },
                        truncateName(
                          user.displayName?.() || user.username?.(),
                          10,
                        ),
                      ),
                      m("div", { className: "CirklaUser-badge" }, [
                        icon("fas fa-comment"),
                        m("span", String(user.commentCount?.() ?? "-")),
                      ]),
                    ],
                  ),
                ),
              ]),
        ]),
        m("div", { className: "CirklaSmallFooter" }, [
          m(
            "div",
            { className: "CirklaMuted" },
            t("pagination", {
              current: slides.length ? this.idx + 1 : 0,
              total: slides.length,
            }),
          ),
        ]),
      ],
    );
  }
}

/**
 * Small data container for the authenticated dashboard.
 *
 * All API calls are isolated here so the IndexPage extension only needs to
 * trigger loading and render the current state.
 */
class CirklaHomeState {
  constructor() {
    this.loading = false;
    this.loaded = false;
    this.slider = [];
    this.events = [];
    this.most = [];
    this.users = [];
  }

  async load() {
    if (this.loading) return;

    this.loading = true;
    redraw();

    // Keep failed cards from breaking the entire dashboard.
    const safeFind = async (type, params) => {
      try {
        return (await app.store.find(type, params)) || [];
      } catch (error) {
        console.warn("[cirkla-home] fetch failed", type, params, error);
        return [];
      }
    };

    try {
      this.slider = await safeFind("discussions", {
        include: "user,tags,lastPostedUser",
        sort: "-createdAt",
        "page[limit]": LIMIT_SLIDER,
        "filter[tag]": forumSetting(
          "cirkla.home.announcements_tag",
          ANNOUNCEMENT_TAG_SLUG,
        ),
      });

      this.events = forumBoolSetting("cirkla.home.show_events", false)
        ? await safeFind("discussions", {
            include: "user,tags,lastPostedUser",
            sort: "-createdAt",
            "page[limit]": LIMIT_LIST,
            "filter[tag]": EVENTS_TAG_SLUG,
          })
        : [];

      this.most = await safeFind("discussions", {
        include: "user,tags,lastPostedUser",
        sort: "-commentCount",
        "page[limit]": LIMIT_LIST,
      });

      if (!this.most.length) this.most = this.events;

      this.users = await safeFind("users", {
        sort: "-commentCount",
        "page[limit]": LIMIT_USERS,
      });
    } finally {
      this.loading = false;
      this.loaded = true;
      redraw();
    }
  }

  ensureLoaded() {
    if (!this.loaded && !this.loading) this.load();
  }
}

// -----------------------------------------------------------------------------
// IndexPage VDOM injection helpers
// -----------------------------------------------------------------------------

function injectAboveDiscussionList(vdom, nodeToInsert) {
  const walk = (node) => {
    if (!node || typeof node !== "object") return false;

    const cls = node.attrs?.className || node.attrs?.class || "";
    const isResults =
      typeof cls === "string" && cls.includes("IndexPage-results");

    if (isResults && Array.isArray(node.children)) {
      const existingClass = node.attrs?.className || node.attrs?.class || "";
      if (
        typeof existingClass === "string" &&
        !existingClass.includes("CirklaHomeResults")
      ) {
        node.attrs = {
          ...(node.attrs || {}),
          className: `${existingClass} CirklaHomeResults`.trim(),
        };
      }

      const existingStyle = node.attrs?.style;
      node.attrs = {
        ...(node.attrs || {}),
        style:
          typeof existingStyle === "string"
            ? `${existingStyle};width:100%;max-width:100%;min-width:0;flex:1 1 0%;`
            : {
                ...(existingStyle || {}),
                width: "100%",
                maxWidth: "100%",
                minWidth: "0",
                flex: "1 1 0%",
              },
      };

      const idx = node.children.findIndex(
        (child) => child && child.attrs && child.attrs.key === "cirkla-home",
      );
      if (idx === -1) node.children.unshift(nodeToInsert);
      else node.children[idx] = nodeToInsert;
      return true;
    }

    if (Array.isArray(node.children)) {
      for (const child of node.children) {
        if (walk(child)) return true;
      }
    }

    return false;
  };

  walk(vdom);
}

function injectGuestShell(vdom, nodeToInsert) {
  const walk = (node) => {
    if (!node || typeof node !== "object") return false;

    const cls = node.attrs?.className || node.attrs?.class || "";
    const isResults =
      typeof cls === "string" && cls.includes("IndexPage-results");

    if (isResults && Array.isArray(node.children)) {
      node.children = [nodeToInsert];
      node.attrs = {
        ...(node.attrs || {}),
        className: `${cls} CirklaGuestResults`.trim(),
      };
      return true;
    }

    if (Array.isArray(node.children)) {
      for (const child of node.children) {
        if (walk(child)) return true;
      }
    }

    return false;
  };

  walk(vdom);
}

// -----------------------------------------------------------------------------
// Flarum initializers
// -----------------------------------------------------------------------------

app.initializers.add("htwsaar-htw-theme-home", () => {
  extend(IndexPage.prototype, "oninit", function () {
    if (app.current?.get?.("routeName") !== "index") return;

    if (isGuest()) {
      homeState = null;
      return;
    }

    if (!currentUserEmailConfirmed()) {
      homeState = null;
      return;
    }

    if (!homeState) homeState = new CirklaHomeState();
    homeState.ensureLoaded();
  });

  extend(IndexPage.prototype, "view", function (vdom) {
    if (app.current?.get?.("routeName") !== "index") return;

    if (isGuest()) {
      if (typeof document !== "undefined") {
        document.body.classList.add("CirklaGuestLocked");
      }
      injectGuestShell(vdom, guestShell());
      return;
    }

    if (!currentUserEmailConfirmed()) {
      homeState = null;
      injectGuestShell(vdom, emailConfirmationShell());
      return;
    }

    if (!homeState) homeState = new CirklaHomeState();
    homeState.ensureLoaded();

    const loading = !homeState.loaded || homeState.loading;

    const home = m(
      "section",
      {
        className: `CirklaHome${loading ? " is-loading" : ""}`,
        style: { width: "100%", maxWidth: "100%", minWidth: "0" },
        key: "cirkla-home",
      },
      [
        m(
          "div",
          {
            className: "CirklaHome-row",
            style: { width: "100%", maxWidth: "100%", minWidth: "0" },
          },
          [m(CirklaCarousel, { items: homeState.slider, loading })],
        ),
        m(
          "div",
          {
            className: "CirklaHome-grid",
            style: { width: "100%", maxWidth: "100%", minWidth: "0" },
          },
          [
            forumBoolSetting("cirkla.home.show_events", false)
              ? m(CirklaSmallDiscussionCarousel, {
                  title: trans("sidebar", "events"),
                  items: homeState.events,
                  titleLines: 3,
                  loading,
                })
              : null,
            m(CirklaSmallDiscussionCarousel, {
              title: t("mostDiscussed"),
              items: homeState.most,
              titleLines: 5,
              loading,
            }),
            m(CirklaUsersCarousel, {
              title: t("topContributors"),
              users: homeState.users,
              loading,
            }),
          ],
        ),
      ],
    );

    injectAboveDiscussionList(vdom, home);
  });
});

app.initializers.add("htwsaar-htw-theme-guest-lock", () => {
  installCrossTabAuthSync();
  installSessionBroadcastHooks();

  const applyGuestLock = () => {
    syncAuthBodyClasses();
    captureNextUrlFromQuery();
    installSessionLoginHook();
    enforceGlobalGuestLock();
    setTimeout(enforceGlobalGuestLock, 0);
    setTimeout(enforceGlobalGuestLock, 120);
  };

  applyGuestLock();

  if (m && m.route && typeof m.route.set === "function") {
    const originalRouteSet = m.route.set;
    m.route.set = function () {
      const result = originalRouteSet.apply(this, arguments);
      applyGuestLock();
      return result;
    };
  }

  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(() => enforceGlobalGuestLock()).observe(
      document.body,
      {
        childList: true,
        subtree: true,
      },
    );
  }

  if (!guestLockHeartbeat) {
    guestLockHeartbeat = setInterval(() => {
      enforceGlobalGuestLock();
    }, 400);
  }
});
